import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAuth } from "../../auth/authContext.tsx";
import { setOnUserQuery } from "../../api/client.ts";
import { superAdminToolDefinitions, type SuperAdminToolDefinition } from "./superAdminToolRegistry.ts";
import type { GridAttributes } from "../../types/grid.ts";
import type { WebAdminPage, WebAdminPageData, ColumnDescription } from "../../types/session.ts";

export interface CapturedQuery {
  userQuery: string;
  method: string;
  label: string;
  timestamp: number;
}

export interface ActiveGridInfo {
  name: string;
  pageDef?: WebAdminPage;
  pageData?: WebAdminPageData;
  attributes?: GridAttributes;
  colInfoMap: Record<string, ColumnDescription>;
}

interface SuperAdminPanelContextValue {
  isOpen: boolean;
  activeToolId: string;
  openPanel: (toolId?: string) => void;
  closePanel: () => void;
  switchTool: (toolId: string) => void;
  latestQuery: CapturedQuery | null;
  /** True when the user may open the panel: full super admins OR restricted SuperUserTools users. */
  hasToolAccess: boolean;
  /** The tools visible to this user (full list for super admins, subset for SuperUserTools). */
  tools: SuperAdminToolDefinition[];
  /** When true, SA toggle button and impersonation banner are hidden (for clean screenshots). */
  saHidden: boolean;
  setSaHidden: (hidden: boolean) => void;
  activeGrids: Map<string, ActiveGridInfo>;
  registerGrid: (info: ActiveGridInfo) => void;
  unregisterGrid: (name: string) => void;
}

const SuperAdminPanelContext = createContext<SuperAdminPanelContextValue | null>(null);

export function useSuperAdminPanel(): SuperAdminPanelContextValue {
  const ctx = useContext(SuperAdminPanelContext);
  if (!ctx) throw new Error("useSuperAdminPanel must be used within SuperAdminPanelProvider");
  return ctx;
}

export function SuperAdminPanelProvider({ children }: { children: ReactNode }) {
  const { realSuperAdmin, realSuperUserTools, cache } = useAuth();
  // Server-authoritative flags. realSuperAdmin grants the full tool set; SuperUserTools
  // (a real DB flag delivered in the cache payload) grants only the restricted subset.
  const superUserTools = realSuperUserTools || cache?.SuperUserTools === true;
  const hasToolAccess = realSuperAdmin || superUserTools;

  // The tools this user may see. Full super admins get everything; SuperUserTools users
  // get only the flagged subset. Server permission checks remain the real gate.
  const tools = useMemo<SuperAdminToolDefinition[]>(
    () => (realSuperAdmin ? superAdminToolDefinitions : superAdminToolDefinitions.filter(t => t.superUserTools)),
    [realSuperAdmin],
  );

  const [isOpen, setIsOpen] = useState(false);
  const [activeToolId, setActiveToolId] = useState(superAdminToolDefinitions[0]?.id ?? "");
  const [saHidden, setSaHidden] = useState(() => sessionStorage.getItem("saHidden") === "true");
  const [latestQuery, setLatestQuery] = useState<CapturedQuery | null>(null);
  // Query Viewer capture is a full-super-admin-only feature (not part of the subset).
  const isSuperAdminRef = useRef(realSuperAdmin);
  isSuperAdminRef.current = realSuperAdmin;

  // Register userQuery callback when full SuperAdmin (Query Viewer is not in the subset)
  useEffect(() => {
    if (!realSuperAdmin) {
      setOnUserQuery(null);
      return;
    }
    setOnUserQuery((userQuery, method, args) => {
      if (isSuperAdminRef.current) {
        const gridName = typeof args?.name === "string" ? args.name : undefined;
        const label = gridName
          ? gridName + (method === "GetDataGridHeader" ? " (header)" : "")
          : method;
        setLatestQuery({ userQuery, method, label, timestamp: Date.now() });
      }
    });
    return () => setOnUserQuery(null);
  }, [realSuperAdmin]);

  // Grid registry for Page Information tool
  const [activeGrids, setActiveGrids] = useState<Map<string, ActiveGridInfo>>(() => new Map());

  const registerGrid = useCallback((info: ActiveGridInfo) => {
    setActiveGrids(prev => {
      const next = new Map(prev);
      next.set(info.name, info);
      return next;
    });
  }, []);

  const unregisterGrid = useCallback((name: string) => {
    setActiveGrids(prev => {
      if (!prev.has(name)) return prev;
      const next = new Map(prev);
      next.delete(name);
      return next;
    });
  }, []);

  const openPanel = useCallback((toolId?: string) => {
    if (toolId) setActiveToolId(toolId);
    setIsOpen(true);
  }, []);

  const closePanel = useCallback(() => setIsOpen(false), []);

  const switchTool = useCallback((toolId: string) => {
    setActiveToolId(toolId);
    setIsOpen(true);
  }, []);

  // Keyboard shortcuts: Ctrl+Shift+Alt+<key>. The triple-modifier prefix is
  // intentional — it sits clear of every browser default and of AltGr-driven
  // dead keys on European layouts (e.g. Ctrl+Alt+Q types `@` on Swedish).
  useEffect(() => {
    if (!hasToolAccess) return;
    function onKeyDown(e: KeyboardEvent) {
      if (!e.ctrlKey || !e.shiftKey || !e.altKey) return;
      // e.key on European AltGr-pressed letters often returns the dead-key
      // glyph (€, @ etc.) rather than the letter. e.code stays as "KeyQ".
      const code = e.code;
      const key = code.startsWith("Key") ? code.slice(3) : code.toUpperCase();
      const tool = tools.find((t) => t.shortcutKey === key);
      if (tool) {
        e.preventDefault();
        setActiveToolId(tool.id);
        setIsOpen(true);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [hasToolAccess, tools]);

  const handleSetSaHidden = useCallback((hidden: boolean) => {
    setSaHidden(hidden);
    sessionStorage.setItem("saHidden", String(hidden));
  }, []);

  const value = useMemo(
    () => ({ isOpen, activeToolId, openPanel, closePanel, switchTool, latestQuery, hasToolAccess, tools, saHidden, setSaHidden: handleSetSaHidden, activeGrids, registerGrid, unregisterGrid }),
    [isOpen, activeToolId, openPanel, closePanel, switchTool, latestQuery, hasToolAccess, tools, saHidden, handleSetSaHidden, activeGrids, registerGrid, unregisterGrid],
  );

  return <SuperAdminPanelContext.Provider value={value}>{children}</SuperAdminPanelContext.Provider>;
}
